import { useEffect, useMemo, useState } from "react";
import { cardPath } from "../lib/paths.js";
import { usePageTitle } from "../hooks/usePageTitle.js";
import { useParams, Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { getSet, getCardsForSet, getOwnedInSet, getWatchlistIds, addSealedProduct } from "../api.js";
import { useAuth } from "../context/AuthContext.jsx";
import WatchlistHeart from "../components/WatchlistHeart.jsx";
import SealedProductDialog from "../components/SealedProductDialog.jsx";
import SealedPrices from "../components/SealedPrices.jsx";
import { eur } from "../lib/format.js";
import Icon from "../components/Icon.jsx";
import { rarityKey, rarityLabel, rarityRank } from "../lib/rarity.js";

const SORTS = {
  number: "Nummer",
  rarity: "Seltenheit (aufsteigend)",
  popularity: "Beliebtheit",
  price_desc: "Preis (hoch → niedrig)",
  price_asc: "Preis (niedrig → hoch)",
};

// Route: /sets/:setId  – frei zugänglich. Angemeldet sind Karten, die du
// besitzt, hervorgehoben; ein Filter zeigt wahlweise nur besessene oder
// nur fehlende. Ohne Konto einfach die Kartenübersicht des Sets.
export default function SetDetail() {
  const { user } = useAuth();
  const { setId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [set, setSet] = useState(null);
  const [cards, setCards] = useState(null);
  usePageTitle(set ? `${set.name} – Kartenliste & Preise | mycardfolio` : null);
  const [owned, setOwned] = useState(new Set());
  const [watchedIds, setWatchedIds] = useState(new Set());
  // Filter stehen in der Adresse (?besitz=&sortierung=&seltenheit=), damit sie nach Karte -> Zurück noch
  // gesetzt sind (Justus 23.10.2026). replace: Filterwechsel legen keine eigenen Verlaufseinträge an.
  const [params, setParams] = useSearchParams();
  const filter = params.get("besitz") ?? "all"; // all | have | missing
  const sort = SORTS[params.get("sortierung")] ? params.get("sortierung") : "number";
  const rarity = params.get("seltenheit") ?? "all";
  const setParam = (key, value, fallback) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (value === fallback) next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: true });
  const setFilter = (v) => setParam("besitz", v, "all");
  const setSort = (v) => setParam("sortierung", v, "number");
  const setRarity = (v) => setParam("seltenheit", v, "all");
  const [sealedOpen, setSealedOpen] = useState(false);
  const [sealedBusy, setSealedBusy] = useState(false);
  const [sealedError, setSealedError] = useState(null);
  const [sealedAdded, setSealedAdded] = useState(false);

  // alte ID-Adresse (/sets/me1) -> lesbare Adresse (/sets/mega-evolution)
  useEffect(() => {
    if (set?.slug && setId !== set.slug) navigate(`/sets/${set.slug}${location.search}`, { replace: true });
  }, [set, setId, navigate, location.search]);

  useEffect(() => {
    getSet(setId).then(setSet);
    if (user) {
      getOwnedInSet(setId).then((ids) => setOwned(new Set(ids))).catch(() => setOwned(new Set()));
      getWatchlistIds().then((ids) => setWatchedIds(new Set(ids))).catch(() => {});
    } else {
      setOwned(new Set());
      setWatchedIds(new Set());
    }

    // Preise kommen nur noch einmal täglich (1 Uhr) - kein Nachladen mehr nötig.
    let cancelled = false;
    getCardsForSet(setId).then((data) => !cancelled && setCards(data));
    return () => {
      cancelled = true;
    };
  }, [setId, user]);

  const shown = useMemo(() => {
    if (!cards) return [];
    let list = cards;
    if (filter === "have") list = list.filter((c) => owned.has(c.external_id));
    else if (filter === "missing") list = list.filter((c) => !owned.has(c.external_id));
    if (rarity !== "all") list = list.filter((c) => rarityKey(c.rarity) === rarity);

    const byNumber = (a, b) => (a.number ?? "").localeCompare(b.number ?? "", undefined, { numeric: true });
    const cmp = {
      number: byNumber,
      rarity: (a, b) => rarityRank(a.rarity) - rarityRank(b.rarity) || byNumber(a, b),
      popularity: (a, b) => (b.view_count ?? 0) - (a.view_count ?? 0),
      price_desc: (a, b) => (b.price ?? -1) - (a.price ?? -1),
      price_asc: (a, b) => {
        // Karten ohne Preis ans Ende, nicht künstlich als "billigste" vorne
        if (a.price == null) return 1;
        if (b.price == null) return -1;
        return a.price - b.price;
      },
    }[sort];
    return [...list].sort(cmp);
  }, [cards, owned, filter, sort, rarity]);

  // Nur Seltenheiten, die es in diesem Set gibt, von gewöhnlich nach selten
  const rarities = useMemo(() => {
    const m = new Map();
    for (const c of cards ?? []) {
      const key = rarityKey(c.rarity);
      const r = m.get(key) ?? { key, label: rarityLabel(c.rarity, set?.name), rank: rarityRank(c.rarity), n: 0 };
      r.n += 1;
      m.set(key, r);
    }
    return [...m.values()].sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label));
  }, [cards, set]);

  const total = cards?.length ?? 0;
  const have = cards ? cards.filter((c) => owned.has(c.external_id)).length : 0;

  async function confirmSealed(values) {
    setSealedBusy(true);
    setSealedError(null);
    try {
      await addSealedProduct({ ...values, setId: set?.id ?? setId, setName: set?.name });
      setSealedOpen(false);
      setSealedAdded(true);
      setTimeout(() => setSealedAdded(false), 3000);
    } catch (err) {
      setSealedError(err.message || "Speichern fehlgeschlagen. Bitte nochmal versuchen.");
    } finally {
      setSealedBusy(false);
    }
  }

  const btn = (v, label) => (
    <button
      onClick={() => setFilter(v)}
      className={`text-xs px-3 py-1.5 rounded-full border ${
        filter === v ? "border-ink text-ink" : "border-line text-subtle hover:border-ink"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div>
      <Link to="/sets" className="text-sm text-subtle hover:text-ink">← Alle Sets</Link>

      {set && (
        <div className="flex items-center gap-4 mt-4 mb-4">
          {(set.logo || set.symbol) && (
            <img src={set.logo ?? set.symbol} alt={set.name} className="h-10 object-contain" />
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold">{set.name}</h1>
            <p className="text-subtle text-xs">
              {set.series} · {set.total} Karten
              {set.release_date ? ` · veröffentlicht ${set.release_date}` : ""}
            </p>
          </div>
          {user && (
            <button
              onClick={() => setSealedOpen(true)}
              className="shrink-0 text-xs border border-line rounded-full px-3 py-1.5 hover:border-ink"
            >
              Sealed-Produkt hinzufügen
            </button>
          )}
        </div>
      )}
      {sealedAdded && (
        <p className="text-mint text-xs mb-4">
          Hinzugefügt – in deiner{" "}
          <Link to="/sammlung" className="underline">Sammlung</Link> sichtbar.
        </p>
      )}

      <SealedPrices sealed={set?.sealed} />

      {cards && user && (
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <p className="text-sm font-medium">
              Du hast <span className="font-mono">{have}</span> von{" "}
              <span className="font-mono">{total}</span>
            </p>
            <div className="flex-1 h-2 rounded-full bg-line overflow-hidden max-w-xs">
              <div
                className="h-full bg-yellow"
                style={{ width: total ? `${(have / total) * 100}%` : 0 }}
              />
            </div>
          </div>
          <div className="flex gap-2">
            {btn("all", `Alle (${total})`)}
            {btn("have", `Hab ich (${have})`)}
            {btn("missing", `Fehlt mir (${total - have})`)}
          </div>
        </div>
      )}
      {cards && !user && (
        <p className="text-subtle text-sm mb-6">
          {total} Karten in diesem Set.{" "}
          <Link to="/register" className="underline hover:text-ink">
            Anmelden
          </Link>
          , um deinen Sammlungsfortschritt zu sehen.
        </p>
      )}

      {cards && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
          <label className="flex items-center gap-1.5">
            <span className="text-xs text-subtle">Sortieren:</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="border border-line rounded-full px-3 py-1.5 text-xs bg-canvas text-ink focus:outline-none focus:border-ink"
            >
              {Object.entries(SORTS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>
          {rarities.length > 1 && (
            <label className="flex items-center gap-1.5">
              <span className="text-xs text-subtle">Seltenheit:</span>
              <select
                value={rarity}
                onChange={(e) => setRarity(e.target.value)}
                className="border border-line rounded-full px-3 py-1.5 text-xs bg-canvas text-ink focus:outline-none focus:border-ink"
              >
                <option value="all">Alle ({total})</option>
                {rarities.map((r) => (
                  <option key={r.key} value={r.key}>{r.label} ({r.n})</option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {cards === null ? (
        <p className="text-subtle text-sm">Lade Karten …</p>
      ) : shown.length === 0 ? (
        <p className="text-subtle text-sm">Keine Karte in dieser Ansicht.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
          {shown.map((card) => {
            const has = owned.has(card.external_id);
            return (
              <Link
                key={card.external_id}
                to={cardPath(card)}
                className="flex flex-col"
              >
                <div className="relative">
                  <img
                    src={card.image_small}
                    alt={`${card.name} (Englisch)`}
                    className={`rounded-2xl border mb-1.5 shadow-sm transition ${has ? "border-yellow" : "border-line"}`}
                  />
                  {/* Besessene Karten: leichter gelber Schleier + Häkchen (Justus 23.10.2026); fehlende normal, nicht ausgegraut */}
                  {has && (
                    <>
                      <span aria-hidden="true" className="absolute inset-x-0 top-0 bottom-1.5 rounded-2xl bg-yellow pointer-events-none" style={{ opacity: 0.18 }} />
                      <span className="absolute top-1 right-1 bg-yellow text-yellowInk rounded-full w-6 h-6 flex items-center justify-center shadow" title="In deiner Sammlung">
                        <Icon name="check" className="w-4 h-4" />
                      </span>
                    </>
                  )}
                  {user && !has && (
                    <WatchlistHeart
                      externalId={card.external_id}
                      watched={watchedIds.has(card.external_id)}
                      onChange={(now) =>
                        setWatchedIds((prev) => {
                          const next = new Set(prev);
                          now ? next.add(card.external_id) : next.delete(card.external_id);
                          return next;
                        })
                      }
                      className="absolute top-1.5 right-1.5 bg-canvas rounded-full w-8 h-8 flex items-center justify-center border border-line shadow-md"
                    />
                  )}
                </div>
                <p className="text-xs font-medium truncate">{card.name}</p>
                <div className="flex items-center justify-between">
                  <p className="text-[11px] text-subtle">#{card.number}</p>
                  <p className="text-[11px] font-mono">{card.price != null ? eur(card.price) : "—"}</p>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {sealedOpen && (
        <SealedProductDialog
          setName={set?.name}
          busy={sealedBusy}
          error={sealedError}
          onConfirm={confirmSealed}
          onClose={() => !sealedBusy && (setSealedOpen(false), setSealedError(null))}
        />
      )}
    </div>
  );
}
